import type { TaskComment } from '@frontend/api-types';
import { dateFormatters, extractSecureNoteIdFromUrl, extractSecureNoteTitle, match } from '@frontend/duck-tape';
import { Fragment, useFeatureIsOn, useState } from '@frontend/react';
import { useVisibilityController } from '@frontend/react/hooks';
import { RenderHTML, RenderMarkdown, Text, XStack, YStack } from '@frontend/web-react';
import { applyClass, concatClasses, safeCss } from '@frontend/web-utils';
import { UpdateSecureNoteModal } from '@frontend/web/components';

export type PlainWhisperProps = Pick<TaskComment, 'body'> & {
  contentType?: 'html' | 'markdown';
  createdAt?: DateTimeISOString;
  isLineDotted?: boolean;
  isLineVisible?: boolean;
  taskId?: string;
};

export const PlainWhisper = ({
  body,
  contentType = 'html',
  createdAt,
  isLineDotted,
  isLineVisible,
  taskId,
}: PlainWhisperProps) => {
  const { enabled: isGameplanPricingEnabled } = useFeatureIsOn('is_gameplan_pricing_enabled');
  const [isSecureNoteCardModalOpen, { close: closeSecureNoteModal, open: openSecureNoteModal }] =
    useVisibilityController();
  const [secureNoteInfo, setSecureNoteInfo] = useState<{ id: string; title: string }>();

  const handleAnchorClick = (href: string) => {
    if (href.includes('secure-note')) {
      const noteId = extractSecureNoteIdFromUrl(href);
      if (noteId) {
        const title = extractSecureNoteTitle(body, noteId, { format: contentType });
        setSecureNoteInfo({ id: noteId, title: title || '' });
        openSecureNoteModal();
      }
    } else if (href.includes('settings/feedback')) {
      const url = taskId ? `/app/settings/feedback?taskId=${taskId}` : href;
      window.open(url, '_blank');
    } else {
      window.open(href, '_blank');
    }
  };

  const baseWhisper = (
    <YStack className="gap-xs">
      <XStack className="gap-sm">
        {match(contentType)
          .with('html', () => (
            <RenderHTML
              anchorClassName="text-p-sm text-textPrimary inline-flex items-center"
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className="mt-[-8px]"
              html={body.replaceAll('zd-comment', safeCss('text-p-sm text-textPrimary'))}
              onAnchorClick={handleAnchorClick}
            />
          ))
          .with('markdown', () => (
            <RenderMarkdown
              className="duckbill-markdown prose max-w-full text-p-sm text-textPrimary"
              onLinkClick={handleAnchorClick}
            >
              {body}
            </RenderMarkdown>
          ))
          .exhaustive()}
      </XStack>
      {isLineVisible ? (
        <YStack
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className={concatClasses(
            'h-[24px] w-[1px] border-neutral-300 border-l-1 ml-[4px]',
            applyClass(isLineDotted, 'border-dashed'),
          )}
        />
      ) : null}
    </YStack>
  );

  const legacyWhisper = (
    <YStack className="gap-xs">
      <XStack className="gap-sm">
        <XStack>
          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
          <XStack className="size-[9px] rounded-full border-1 border-borderTertiary" />
        </XStack>
        {match(contentType)
          .with('html', () => (
            <RenderHTML
              anchorClassName="text-p-sm text-textPrimary inline-flex items-center"
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className="mt-[-8px]"
              html={
                createdAt
                  ? body
                      .replaceAll('zd-comment', safeCss('text-p-sm text-textTertiary'))
                      .replaceAll(
                        '</div>',
                        `<small class="text-p-xs text-neutral-400"> at ${dateFormatters['hh:mm a'](createdAt)}</small></div>`,
                      )
                  : body.replaceAll('zd-comment', safeCss('text-p-sm text-textTertiary'))
              }
              onAnchorClick={handleAnchorClick}
            />
          ))
          .with('markdown', () => (
            <YStack className="gap-xs">
              <RenderMarkdown
                className="duckbill-markdown prose max-w-full text-p-sm text-textTertiary"
                onLinkClick={handleAnchorClick}
              >
                {body}
              </RenderMarkdown>
              {createdAt ? (
                <Text className="text-neutral-400" type="caption">
                  {' '}
                  at {dateFormatters['hh:mm a'](createdAt)}
                </Text>
              ) : null}
            </YStack>
          ))
          .exhaustive()}
      </XStack>
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value,tailwindcss/no-custom-classname */}
      {isLineVisible ? (
        <YStack
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className={concatClasses(
            'h-[24px] w-[1px] border-neutral-300 border-l-1 ml-[4px]',
            applyClass(isLineDotted, 'border-dashed'),
          )}
        />
      ) : null}
    </YStack>
  );

  const content = isGameplanPricingEnabled ? baseWhisper : legacyWhisper;

  return (
    <Fragment>
      {content}
      {secureNoteInfo?.id ? (
        <UpdateSecureNoteModal
          isOpen={isSecureNoteCardModalOpen}
          onClose={closeSecureNoteModal}
          secureNoteId={secureNoteInfo.id}
          taskId={taskId}
          title={secureNoteInfo.title}
        />
      ) : null}
    </Fragment>
  );
};
